import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from "../config";
import { toast } from 'react-toastify';
import {
  FaClipboardList, FaTruck, FaChartLine, FaFileInvoice,
  FaUserCheck, FaPlusCircle, FaSpinner, FaSyncAlt, FaMoneyBillWave,
  FaRobot, FaCalendarAlt, FaHistory, FaExclamationTriangle, FaClock, FaHandHoldingUsd
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, Tooltip, Legend, Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, Tooltip, Legend, Filler
);

const NAVY = '#0A1929';
const GREEN = '#00A86B';
const PALETTE = ['#00A86B', '#3B82F6', '#F59E0B', '#EC4899', '#7C3AED', '#0EA5E9', '#EF4444', '#14B8A6', '#A855F7', '#64748B'];

const money = (n) => `R ${(Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money0 = (n) => `R ${Math.round(Number(n) || 0).toLocaleString('en-ZA')}`;
const compact = (n) => {
  const v = Number(n) || 0;
  const a = Math.abs(v);
  if (a >= 1000000) return `R ${(v / 1000000).toFixed(1)}m`;
  if (a >= 1000) return `R ${Math.round(v / 1000)}k`;
  return `R ${Math.round(v)}`;
};

// Short day label for the upcoming-jobs list.
const fmtDay = (v) => {
  if (!v) return '—';
  const d = new Date(`${v}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-ZA', { weekday: 'short', day: '2-digit', month: 'short' });
};

// "3h ago" for the activity feed. DB stores naive UTC timestamps, so parse them as UTC.
const timeAgo = (v) => {
  if (!v) return '';
  const iso = /Z|[+-]\d\d:\d\d$/.test(String(v)) ? v : `${v}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

// Chart.js has no built-in "total in the middle of the doughnut", so this draws it.
const centreTextPlugin = {
  id: 'centreText',
  afterDraw(chart, _args, opts) {
    if (!opts || !opts.enabled) return;
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const x = (chartArea.left + chartArea.right) / 2;
    const y = (chartArea.top + chartArea.bottom) / 2;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = NAVY;
    ctx.font = 'bold 28px Helvetica, Arial, sans-serif';
    ctx.fillText(String(opts.value == null ? '' : opts.value), x, y - 8);
    ctx.fillStyle = '#64748B';
    ctx.font = '11px Helvetica, Arial, sans-serif';
    ctx.fillText(opts.label || '', x, y + 16);
    ctx.restore();
  }
};

// Small segmented control for the cards that can change period / scope.
function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex rounded-lg p-0.5" style={{ backgroundColor: '#F1F5F9' }}>
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
          style={value === o.value
            ? { backgroundColor: '#FFFFFF', color: NAVY, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }
            : { color: '#64748B' }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Card({ title, subtitle, action, children, className }) {
  return (
    <div className={`bg-white rounded-xl border shadow-sm p-5 ${className || ''}`} style={{ borderColor: '#E8ECEF' }}>
      {(title || action) && (
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            {title && <h3 className="text-sm font-bold" style={{ color: NAVY }}>{title}</h3>}
            {subtitle && <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function Kpi({ label, value, sub, icon, accent, subColor, title }) {
  return (
    <div className="rounded-xl border shadow-sm p-4 relative overflow-hidden" title={title || undefined}
      style={{ borderColor: '#E8ECEF', background: `linear-gradient(160deg, #FFFFFF 0%, ${accent}0F 100%)` }}>
      <span className="absolute inset-x-0 top-0 h-0.5" style={{ backgroundColor: accent }} />
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium uppercase tracking-wide" style={{ color: '#64748B' }}>{label}</p>
        <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm" style={{ backgroundColor: `${accent}1F`, color: accent }}>{icon}</span>
      </div>
      <p className="text-2xl font-bold mt-2" style={{ color: NAVY }}>{value}</p>
      {sub && <p className="text-xs mt-1" style={{ color: subColor || '#64748B' }}>{sub}</p>}
    </div>
  );
}

const moneyAxis = {
  ticks: { callback: (v) => compact(v), color: '#94A3B8', font: { size: 10 } },
  grid: { color: '#F1F5F9' },
  border: { display: false }
};

const baseTooltip = {
  backgroundColor: NAVY,
  padding: 10,
  cornerRadius: 8,
  titleFont: { size: 11 },
  bodyFont: { size: 11 }
};

// ============================================
// COMPONENT
// ============================================
function AdminDashboard({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [user, setUser] = useState({});
  const [revenueRange, setRevenueRange] = useState('30d');
  const [revMode, setRevMode] = useState('cumulative');
  // Which basis the "Revenue over time" line is drawn on. Received is the default, because it is
  // what the Revenue MTD card above it and the sales-by-salesperson card below it both count.
  const [revBasis, setRevBasis] = useState('received');
  const [quoteRange, setQuoteRange] = useState('7d');
  const [jobScope, setJobScope] = useState('month');
  // Sales by salesperson — PAID money. Its own request, so changing the period or the person
  // never waits on the rest of the dashboard, and switching person is instant (client-side).
  const [salesPaid, setSalesPaid] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesErr, setSalesErr] = useState(null);
  const [salesGran, setSalesGran] = useState('month');
  const [salesPerson, setSalesPerson] = useState('all');
  const [salesPreset, setSalesPreset] = useState('m6');
  const monthInput = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const [salesFrom, setSalesFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 5);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [salesTo, setSalesTo] = useState(() => monthInput(new Date()));
  // Jeeves
  const [jeeves, setJeeves] = useState(null);
  const [jeevesLoading, setJeevesLoading] = useState(false);
  const [jeevesError, setJeevesError] = useState(null);

  // Shorthand for the KPI block (declared up here so everything below can use it).
  const k = (data && data.kpis) || {};

  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem('user') || '{}'));
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ONE call. Every figure comes from the backend already computed, so the page can never
  // disagree with the database (the old dashboard recomputed money in the browser and got it
  // wrong: it read the dead `loads` table, so "Loads" was always 0).
  const load = async (isRefresh = true) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || `Request failed: ${res.status}`);
      setData(json);
      setLoadError(null);
      loadJeeves();
    } catch (err) {
      console.error('Dashboard load failed:', err);
      setLoadError(err.message || 'Could not load the dashboard');
      toast.error(err.message || 'Could not load the dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Sales (PAID) — money RECEIVED, by period. Separate request; the salesperson filter is
  // applied in the browser so switching who you look at needs no round trip.
  const loadSalesPaid = async () => {
    setSalesLoading(true);
    try {
      const from = `${salesFrom}-01`;
      const eom = new Date(Number(salesTo.slice(0, 4)), Number(salesTo.slice(5, 7)), 0);
      const to = `${salesTo}-${String(eom.getDate()).padStart(2, '0')}`;
      const qs = new URLSearchParams({ from, to, granularity: salesGran }).toString();
      const res = await fetch(`${API_URL}/admin/dashboard/sales-paid?${qs}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || `Request failed: ${res.status}`);
      setSalesPaid(json);
      setSalesErr(null);
    } catch (err) {
      console.error('Sales (paid) load failed:', err);
      setSalesErr(err.message || 'Could not load sales');
    } finally {
      setSalesLoading(false);
    }
  };

  // Quick period presets for the sales card (month dropdowns still allow anything).
  const applySalesPreset = (preset) => {
    const now = new Date();
    const to = monthInput(now);
    const back = (n) => monthInput(new Date(now.getFullYear(), now.getMonth() - n, 1));
    setSalesPreset(preset);
    if (preset === 'm1') { setSalesFrom(to); setSalesTo(to); }
    else if (preset === 'm3') { setSalesFrom(back(2)); setSalesTo(to); }
    else if (preset === 'm6') { setSalesFrom(back(5)); setSalesTo(to); }
    else if (preset === 'm12') { setSalesFrom(back(11)); setSalesTo(to); }
    else if (preset === 'ytd') { setSalesFrom(`${now.getFullYear()}-01`); setSalesTo(to); }
  };

  useEffect(() => {
    loadSalesPaid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesFrom, salesTo, salesGran]);

  // Jeeves' narration. Deliberately its OWN request: the dashboard renders instantly, and if
  // the AI is slow or unavailable the page shows its own computed figures with no fuss.
  const loadJeeves = async () => {
    setJeevesLoading(true);
    setJeevesError(null);
    try {
      const res = await fetch(`${API_URL}/admin/dashboard/brief`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`
        }
      });
      const json = await res.json();
      if (json.success && json.jeeves) {
        setJeeves(json.jeeves);
      } else {
        setJeeves(null);
        setJeevesError(json.error || 'Jeeves is momentarily indisposed');
      }
    } catch (err) {
      console.error('Jeeves briefing failed:', err);
      setJeeves(null);
      setJeevesError('Jeeves is momentarily indisposed');
    } finally {
      setJeevesLoading(false);
    }
  };

  // If Jeeves cannot speak, the same facts are shown as plain bullets - the brief is never
  // simply empty.
  const fallbackBullets = data ? [
    `Received ${money0(k.revenue_mtd)} this month${Number(k.invoiced_mtd) > 0 ? ` (${money0(k.invoiced_mtd)} invoiced)` : ''}; ${money0(data.revenue_projected)} projected at the current invoicing pace`,
    data.revenue_week ? `Money in this week ${money0(data.revenue_week.this_week)} against ${money0(data.revenue_week.last_week)} last week` : null,
    data.quotes_week ? `${data.quotes_week.this_week} quote(s) this week against ${data.quotes_week.last_week} last week` : null,
    data.quotes_last_month ? `Last month ${data.quotes_last_month.sent} quote(s) sent, ${data.quotes_last_month.won} accepted` : null,
    k.outstanding_total ? `${money0(k.outstanding_total)} outstanding across ${k.outstanding_count} invoice(s)` : null
  ].filter(Boolean) : [];

  // ---------- revenue over time ----------
  // TWO bases, one bucketing engine on the backend: money RECEIVED (the default — the same basis
  // as the card above and the salesperson chart below) and what we INVOICED. Cumulative by
  // default: a running total within the chosen period, so the line starts at zero and only ever
  // climbs. Plotting raw per-day figures made it dive back to zero on quiet days and looked broken.
  const revSet = (data && (revBasis === 'invoiced' ? data.invoiced_series : data.revenue_series)) || {};
  const revSeriesRaw = revSet[revenueRange] || [];
  let running = 0;
  const revSeries = revSeriesRaw.map((d) => {
    running = Math.round((running + (Number(d.value) || 0)) * 100) / 100;
    return { ...d, cumulative: running };
  });
  const revWord = revBasis === 'invoiced' ? 'invoiced (issued)' : 'received';
  const revenueChart = {
    labels: revSeries.map((d) => d.label),
    datasets: [{
      label: revMode === 'cumulative' ? `Revenue to date (${revWord})` : `Revenue (${revWord})`,
      data: revSeries.map((d) => (revMode === 'cumulative' ? d.cumulative : d.value)),
      borderColor: revBasis === 'invoiced' ? '#3B82F6' : GREEN,
      borderWidth: 2.5,
      fill: true,
      tension: 0.35,
      pointRadius: revSeries.length > 20 ? 0 : 3,
      pointHoverRadius: 5,
      pointBackgroundColor: revBasis === 'invoiced' ? '#3B82F6' : GREEN,
      backgroundColor: (ctx) => {
        const { chart } = ctx;
        const top = revBasis === 'invoiced' ? 'rgba(59,130,246,0.35)' : 'rgba(0,168,107,0.45)';
        const bottom = revBasis === 'invoiced' ? 'rgba(59,130,246,0.02)' : 'rgba(0,168,107,0.02)';
        if (!chart.chartArea) return bottom;
        const g = chart.ctx.createLinearGradient(0, chart.chartArea.top, 0, chart.chartArea.bottom);
        g.addColorStop(0, top);
        g.addColorStop(1, bottom);
        return g;
      }
    }]
  };
  const revenueOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { ...baseTooltip, callbacks: { label: (c) => ` ${money(c.parsed.y)}` } }
    },
    scales: {
      y: { ...moneyAxis, beginAtZero: true },
      x: { ticks: { color: '#94A3B8', font: { size: 10 }, maxRotation: 0, autoSkipPadding: 12 }, grid: { display: false }, border: { display: false } }
    },
    interaction: { mode: 'index', intersect: false }
  };

  // ---------- job status doughnut ----------
  const jobRows = (data && data.job_status && data.job_status[jobScope]) || [];
  const jobTotal = jobRows.reduce((s, r) => s + r.count, 0);
  const jobChart = {
    labels: jobRows.map((r) => r.label),
    datasets: [{
      data: jobRows.map((r) => r.count),
      backgroundColor: PALETTE,
      borderColor: '#FFFFFF',
      borderWidth: 2,
      hoverOffset: 4
    }]
  };
  const doughnutOptions = {
    responsive: true, maintainAspectRatio: false, cutout: '68%',
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 }, color: '#475569', padding: 12 } },
      tooltip: { ...baseTooltip },
      centreText: { enabled: true, value: jobTotal, label: jobScope === 'month' ? 'JOBS THIS MONTH' : 'JOBS ALL TIME' }
    }
  };

  // ---------- top customers ----------
  const topCustomers = (data && data.top_customers) || [];
  const customerChart = {
    labels: topCustomers.map((c) => c.name),
    datasets: [{
      label: 'Revenue (excl VAT)',
      data: topCustomers.map((c) => c.revenue),
      backgroundColor: topCustomers.map((_, i) => PALETTE[i % PALETTE.length]),
      borderRadius: 6,
      barThickness: 18
    }]
  };
  const customerOptions = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { ...baseTooltip, callbacks: { label: (c) => ` ${money(c.parsed.x)}` } }
    },
    scales: {
      x: { ...moneyAxis, beginAtZero: true },
      y: { ticks: { color: '#475569', font: { size: 11 } }, grid: { display: false }, border: { display: false } }
    }
  };

  // ---------- sales by salesperson: PAID money, by period ----------
  // REPLACED the old "allocated work to date" chart, which plotted PRICED LINE ITEMS for all
  // time — a plan, not cash — and could never be filtered. This plots money actually RECEIVED
  // (each payment on the date it arrived), grouped into the period chosen, with the salesperson
  // filter applied instantly in the browser (every person already came back in one call).
  // The bar convention is unchanged: full height = turnover, green = margin, muted = cost.
  const salesBuckets = (salesPaid && salesPaid.buckets) || [];
  const salesAll = (salesPaid && salesPaid.series) || [];
  // The WHOLE team comes back from the backend, so the picker lists every salesperson — not just
  // the ones who happened to receive money in the window. Someone with nothing received is a real
  // answer ("nothing in yet"), not a missing person.
  const salesTeam = [
    ...salesAll.map((s) => ({ key: String(s.id || 'unassigned'), id: s.id, name: s.name, received: Number(s.totals.turnover) || 0 })),
    ...((salesPaid && salesPaid.people) || [])
      .filter((p) => !salesAll.some((s) => String(s.id || 'unassigned') === String(p.id)))
      .map((p) => ({ key: String(p.id), id: p.id, name: p.name, received: 0 }))
  ];
  const colourByKey = (key) => {
    const i = salesTeam.findIndex((t) => t.key === key);
    return PALETTE[(i < 0 ? 0 : i) % PALETTE.length];
  };
  const salesRows = salesPerson === 'all'
    ? salesAll
    : salesAll.filter((s) => String(s.id || 'unassigned') === salesPerson);
  const salesSum = salesBuckets.map((_, i) => salesRows.reduce((acc, s) => {
    const pt = s.points[i] || { turnover: 0, cost: 0, margin: 0 };
    return {
      turnover: acc.turnover + (Number(pt.turnover) || 0),
      cost: acc.cost + (Number(pt.cost) || 0),
      margin: acc.margin + (Number(pt.margin) || 0)
    };
  }, { turnover: 0, cost: 0, margin: 0 }));
  const salesTotals = salesRows.reduce((acc, s) => ({
    turnover: acc.turnover + (Number(s.totals.turnover) || 0),
    margin: acc.margin + (Number(s.totals.margin) || 0),
    payments: acc.payments + (Number(s.totals.payments) || 0)
  }), { turnover: 0, margin: 0, payments: 0 });
  const salesMarginPct = salesTotals.turnover > 0 ? Math.round((salesTotals.margin / salesTotals.turnover) * 1000) / 10 : null;
  // COMPARISON VIEW. "All salespeople" used to collapse the whole team into ONE anonymous stacked
  // bar, so the only question it could answer was "how did we do?" — never "who did it?".
  // Now each person is their own bar, in their own colour, side by side in every period, with the
  // pale segment underneath the colour still reading as cost. Filtering to one person summons
  // their single bar, exactly as before.
  const salesCompare = salesPerson === 'all' && salesAll.length > 1;
  const salesChart = {
    labels: salesBuckets.map((b) => b.label),
    datasets: salesCompare
      ? salesAll.flatMap((s) => {
        const key = String(s.id || 'unassigned');
        const colour = colourByKey(key);
        return [
          {
            label: s.name,
            personKey: key,
            kind: 'margin',
            data: s.points.map((p) => Number(p.margin) || 0),
            backgroundColor: colour,
            borderRadius: 4,
            stack: key
          },
          {
            label: s.name,
            personKey: key,
            kind: 'cost',
            data: s.points.map((p) => Math.max(0, Number(p.cost) || 0)),
            backgroundColor: '#CBD5E1',
            borderRadius: 4,
            stack: key
          }
        ];
      })
      : [
        { label: 'Margin', data: salesSum.map((p) => p.margin), backgroundColor: GREEN, borderRadius: 5, stack: 'turnover' },
        { label: 'Cost', data: salesSum.map((p) => Math.max(0, p.cost)), backgroundColor: '#CBD5E1', borderRadius: 5, stack: 'turnover' }
      ]
  };
  const salesOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top', align: 'end',
        labels: {
          boxWidth: 10, boxHeight: 10, font: { size: 11 }, color: '#475569',
          // One legend entry per salesperson: the pale cost segment is the same colour for
          // everyone (it is not a person), so its entries would be pure noise.
          filter: (item, chartData) => {
            const ds = (chartData && chartData.datasets) ? chartData.datasets[item.datasetIndex] : null;
            return !ds || ds.kind !== 'cost';
          }
        }
      },
      tooltip: {
        ...baseTooltip,
        callbacks: {
          title: (items) => {
            if (!items.length) return '';
            const bucket = salesChart.labels[items[0].dataIndex] || '';
            const ds = salesChart.datasets[items[0].datasetIndex];
            return salesCompare && ds ? `${bucket} — ${ds.label}` : bucket;
          },
          label: (c) => ` ${c.dataset.label}: ${money0(c.parsed.y)}`,
          footer: (items) => {
            if (!items.length) return '';
            const i = items[0].dataIndex;
            if (salesCompare) {
              const ds = salesChart.datasets[items[0].datasetIndex];
              const s = salesAll.find((r) => String(r.id || 'unassigned') === ds.personKey);
              const pt = (s && s.points[i]) || { turnover: 0, margin: 0 };
              const pc = pt.turnover > 0 ? Math.round((pt.margin / pt.turnover) * 1000) / 10 : null;
              return `Received ${money0(pt.turnover)}${pc == null ? '' : ` • margin ${pc}%`}`;
            }
            const p = salesSum[i] || { turnover: 0, margin: 0 };
            const pc = p.turnover > 0 ? Math.round((p.margin / p.turnover) * 1000) / 10 : null;
            return `Received ${money0(p.turnover)}${pc == null ? '' : ` • margin ${pc}%`}`;
          }
        }
      }
    },
    scales: {
      y: { ...moneyAxis, beginAtZero: true, stacked: true },
      x: { stacked: true, ticks: { color: '#475569', font: { size: 11 }, maxRotation: 0, autoSkip: true, autoSkipPadding: 8 }, grid: { display: false }, border: { display: false } }
    }
  };

  // ---------- quote requests per day (busiest day highlighted, as on the register) ----------
  const quoteSeries = (data && data.quote_requests_per_day && data.quote_requests_per_day[quoteRange]) || [];
  const maxQuotes = quoteSeries.length ? Math.max(...quoteSeries.map((d) => d.count)) : 0;
  const quoteChart = {
    labels: quoteSeries.map((d) => d.label),
    datasets: [{
      label: 'Quote requests',
      data: quoteSeries.map((d) => d.count),
      backgroundColor: quoteSeries.map((d) => (maxQuotes > 0 && d.count === maxQuotes ? GREEN : '#CBD5E1')),
      borderRadius: 6
    }]
  };
  const quoteOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { ...baseTooltip } },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0, color: '#94A3B8', font: { size: 10 } }, grid: { color: '#F1F5F9' }, border: { display: false } },
      x: { ticks: { color: '#94A3B8', font: { size: 10 }, maxRotation: 0, autoSkip: true }, grid: { display: false }, border: { display: false } }
    }
  };

  // ---------- receivables ageing ----------
  const ageing = (data && data.receivable_ageing) || { current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90_plus: 0 };
  const ageingRows = [
    { label: 'Not yet due', value: ageing.current, color: '#64748B' },
    { label: '1 – 30 days', value: ageing.d1_30, color: '#F59E0B' },
    { label: '31 – 60 days', value: ageing.d31_60, color: '#F97316' },
    { label: '61 – 90 days', value: ageing.d61_90, color: '#EF4444' },
    { label: '90+ days', value: ageing.d90_plus, color: '#991B1B' }
  ];
  const ageingMax = Math.max(1, ...ageingRows.map((r) => r.value));

  const winRate = (data && data.quote_win_rate) || { approved: 0, rejected: 0, decided: 0, pct: null };
  const onTime = (data && data.on_time) || { scored: 0, on_time: 0, pct: null };
  const fleet = (data && data.fleet) || { trucks: 0, loads_active: 0, utilisation_pct: null };
  const revChange = k.revenue_change_pct;

  // Target vs achieved: the current month's progress, plus twelve months of history so the
  // chart above can be read against the plan.
  const target = (data && data.target) || null;
  const tva = (data && data.target_vs_actual) || [];
  const tvaMax = Math.max(1, ...tva.map((m) => Math.max(m.target || 0, m.actual || 0)));
  const targetGap = target && target.set ? Math.max(0, (target.revenue || 0) - (target.achieved || 0)) : null;

  // Where gross profit's cost comes from — spelled out, because it is the figure people question.
  // Extra and futile trips never appear on a job line; they appear on the subbie's invoice.
  const costBasisTitle =
    `Cost is taken from the subbie invoices raised against the jobs this money belongs to ` +
    `(${money0(k.received_cost_from_subbie_invoices)}), so extra and futile trips are included — ` +
    `not just the cost originally priced on the line items. The job's cost is apportioned in the ` +
    `same proportion as the invoice has been paid.` +
    (k.jobs_awaiting_subbie_invoice
      ? ` ${k.jobs_awaiting_subbie_invoice} job(s) had no subbie invoice yet, so the agreed line ` +
        `cost was used for those (${money0(k.received_cost_from_line_items)}).`
      : '');

  // What the turnover card counts — the one figure on this page that is most easily misread.
  const cashBasisTitle =
    `Money actually RECEIVED this month, excluding VAT, dated by the day it arrived ` +
    `(${k.received_payments || 0} payment(s)). An invoice issued in September but settled in ` +
    `October counts here, in October — so a quiet invoicing month with a real receipt is not R0.` +
    (Number(k.invoiced_mtd) > 0
      ? ` For reference, ${money0(k.invoiced_mtd)} was invoiced (issued) this month.`
      : ` Nothing was invoiced this month; the money above is from invoices issued earlier.`);

  if (loading) {
    return (
      <AdminLayout onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <FaSpinner className="animate-spin text-3xl" style={{ color: GREEN }} />
        </div>
      </AdminLayout>
    );
  }

  // A failed load must never masquerade as a real result. Previously a 500 rendered every card as
  // R0 behind a toast that faded after three seconds, so a backend fault read as "we invoiced
  // nothing this month" — which is exactly how the broken dashboard was reported. Say so instead.
  // (Only when there is no data at all: if a background REFRESH fails, the previous figures stay
  // on screen untouched and the toast is enough.)
  if (loadError && !data) {
    return (
      <AdminLayout onLogout={onLogout}>
        <div className="rounded-xl border p-8 text-center max-w-lg mx-auto mt-16 shadow-sm"
          style={{ borderColor: '#FECACA', backgroundColor: '#FEF2F2' }}>
          <p className="text-base font-semibold mb-2" style={{ color: '#B91C1C' }}>The dashboard could not load</p>
          <p className="text-sm mb-3 break-words" style={{ color: '#7F1D1D' }}>{loadError}</p>
          <p className="text-xs mb-5" style={{ color: '#B91C1C', opacity: 0.85 }}>
            Nothing on this page would have meant anything — so nothing is shown. A zero here would
            not mean you did no business.
          </p>
          <button onClick={() => load(false)}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white inline-flex items-center gap-2"
            style={{ backgroundColor: NAVY }}>
            <FaSyncAlt size={12} /> Try again
          </button>
          <p className="text-xs mt-4" style={{ color: '#7F1D1D', opacity: 0.75 }}>
            Still failing? The backend log has the exact error.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout onLogout={onLogout}>
      <div className="space-y-5">
        {/* ---------- header ---------- */}
        <div className="rounded-xl px-5 py-4 flex flex-wrap items-center justify-between gap-3 shadow-sm border"
          style={{ borderColor: '#E8ECEF', background: 'linear-gradient(120deg, #FFFFFF 0%, #F0FDF9 45%, #E7F6EF 100%)' }}>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: NAVY }}>
              Good day{user.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}
            </h1>
            <p className="text-sm" style={{ color: '#64748B' }}>
              {data?.period_label || ''} — revenue, customers and fleet at a glance
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/new-jobs" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: GREEN }}>
              <FaPlusCircle /> New Job
            </Link>
            <button onClick={() => load(true)} disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60"
              style={{ backgroundColor: NAVY }}>
              <FaSyncAlt className={refreshing ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {/* ---------- KPI strip ---------- */}
        {/* Order is deliberate: the money reads left to right — turnover, what we kept, the
            percentage of it, then what is still owed — before the operational counts. */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-4">
          <Kpi label="Revenue MTD" value={money0(k.revenue_mtd)}
            sub={revChange == null
              ? 'No comparison yet'
              : `${revChange >= 0 ? '▲' : '▼'} ${Math.abs(revChange)}% vs same days last month`}
            subColor={revChange == null ? '#64748B' : (revChange >= 0 ? GREEN : '#EF4444')}
            title={cashBasisTitle}
            icon={<FaMoneyBillWave />} accent={GREEN} />
          <Kpi label="Gross profit" value={money0(k.received_margin)}
            sub={`${money0(k.received_turnover)} − ${money0(k.received_cost)} cost`}
            title={costBasisTitle}
            icon={<FaHandHoldingUsd />} accent="#0D9488" />
          <Kpi label="Gross margin" value={k.gross_margin_pct == null ? '—' : `${k.gross_margin_pct}%`}
            sub={`on ${money0(k.received_turnover)} received${k.margin_target_pct ? ` · target ${k.margin_target_pct}%` : ''}`}
            title={costBasisTitle}
            icon={<FaChartLine />} accent="#7C3AED" />
          <Kpi label="Outstanding" value={money0(k.outstanding_total)}
            sub={`${k.outstanding_count || 0} invoice(s) unpaid`}
            icon={<FaFileInvoice />} accent="#F59E0B" />
          <Kpi label="Jobs" value={k.jobs_total || 0}
            sub={`${k.jobs_active || 0} active · ${k.jobs_pending || 0} pending`}
            icon={<FaTruck />} accent="#3B82F6" />
          <Kpi label="Loads" value={k.loads_total || 0}
            sub={`${k.loads_active || 0} active`}
            icon={<FaClipboardList />} accent="#EC4899" />
          <Kpi label="Subbies" value={k.subbies || 0}
            sub={`${k.clients || 0} clients`}
            icon={<FaUserCheck />} accent="#0EA5E9" />
        </div>

        {/* ---------- Jeeves' morning brief ---------- */}
        {/* Jeeves narrates the SAME figures computed above. He never calculates anything:
            the numbers come from the backend and he only puts them into English. If he is
            unavailable the identical facts are shown as plain bullets. */}
        <div className="rounded-xl overflow-hidden shadow-sm border" style={{ borderColor: '#E8ECEF' }}>
          <div className="px-5 py-3 flex flex-wrap items-center justify-between gap-3"
            style={{ background: `linear-gradient(120deg, ${NAVY} 0%, #12372A 55%, ${GREEN} 100%)` }}>
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.16)', color: '#FFFFFF' }}>
                <FaRobot size={16} />
              </span>
              <div>
                <p className="text-sm font-bold text-white">Jeeves</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.72)' }}>
                  Your briefing for {data?.period_label || 'this month'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {jeeves && jeeves.mood && (
                <span className="text-xs px-2 py-0.5 rounded-full capitalize" style={{ backgroundColor: 'rgba(255,255,255,0.16)', color: '#FFFFFF' }}>
                  {jeeves.mood}
                </span>
              )}
              <button onClick={loadJeeves} disabled={jeevesLoading}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-60"
                style={{ backgroundColor: 'rgba(255,255,255,0.16)', color: '#FFFFFF' }}>
                <FaSyncAlt className={jeevesLoading ? 'animate-spin' : ''} size={11} />
                {jeevesLoading ? 'Consulting…' : 'Regenerate'}
              </button>
            </div>
          </div>

          <div className="bg-white p-5">
            {jeeves ? (
              <>
                <p className="text-sm leading-relaxed" style={{ color: NAVY }}>{jeeves.summary}</p>
                {(jeeves.observations || []).length > 0 && (
                  <ul className="mt-4 space-y-1.5">
                    {jeeves.observations.map((o, i) => (
                      <li key={i} className="text-sm flex gap-2" style={{ color: '#475569' }}>
                        <span style={{ color: GREEN }}>·</span><span>{o}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {(jeeves.actions || []).length > 0 && (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: '#F1F5F9' }}>
                    <p className="text-xs font-medium uppercase tracking-wide mb-2" style={{ color: '#64748B' }}>Suggested actions</p>
                    <div className="flex flex-wrap gap-2">
                      {jeeves.actions.map((a, i) => (
                        <span key={i} className="text-xs px-3 py-1.5 rounded-lg" style={{ backgroundColor: '#F0FDF9', color: '#065F46' }}>{a}</span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : jeevesLoading ? (
              <p className="text-sm" style={{ color: '#64748B' }}>One moment, sir — I am reading the figures.</p>
            ) : (
              <>
                <p className="text-sm italic mb-3" style={{ color: '#64748B' }}>
                  {jeevesError ? 'Jeeves is momentarily indisposed, sir. The figures, in the meantime:' : 'The figures:'}
                </p>
                <ul className="space-y-1.5">
                  {fallbackBullets.map((b, i) => (
                    <li key={i} className="text-sm flex gap-2" style={{ color: '#475569' }}>
                      <span style={{ color: GREEN }}>·</span><span>{b}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        {/* ---------- revenue over time + job status ---------- */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          <Card
            className="xl:col-span-2"
            title="Revenue over time"
            subtitle={revMode === 'cumulative'
              ? `Running total of ${revBasis === 'invoiced' ? 'invoices issued' : 'money received'} in this period, excluding VAT`
              : `${revBasis === 'invoiced' ? 'Invoices issued' : 'Money received'} per day / week / month, excluding VAT`}
            action={
              <div className="flex items-center gap-2">
                <Segmented value={revBasis} onChange={setRevBasis} options={[
                  { value: 'received', label: 'Received' },
                  { value: 'invoiced', label: 'Invoiced' }
                ]} />
                <Segmented value={revMode} onChange={setRevMode} options={[
                  { value: 'cumulative', label: 'Running total' },
                  { value: 'total', label: 'Per period' }
                ]} />
                <Segmented value={revenueRange} onChange={setRevenueRange} options={[
                  { value: '7d', label: '7D' },
                  { value: '30d', label: '30D' },
                  { value: '90d', label: '90D' },
                  { value: '12m', label: '12M' }
                ]} />
              </div>
            }
          >
            <div style={{ height: 260 }}>
              {revSeries.length
                ? <Line data={revenueChart} options={revenueOptions} />
                : <p className="text-sm text-center pt-16" style={{ color: '#94A3B8' }}>
                  No {revBasis === 'invoiced' ? 'invoices issued' : 'money received'} in this period
                </p>}
            </div>

            {/* ---------- target vs achieved ---------- */}
            {tva.length > 0 && (
              <div className="mt-5 pt-4 border-t" style={{ borderColor: '#F1F5F9' }}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <p className="text-xs font-medium uppercase tracking-wide" style={{ color: '#64748B' }}>
                    Target vs achieved · invoices issued
                  </p>
                  {target && target.set ? (
                    <p className="text-xs font-semibold" style={{ color: (target.pct || 0) >= 100 ? GREEN : NAVY }}>
                      {target.pct == null ? '' : `${target.pct}% of target`}
                    </p>
                  ) : (
                    <Link to="/settings" className="text-xs font-medium" style={{ color: GREEN }}>Set a target</Link>
                  )}
                </div>

                {target && target.set ? (
                  <>
                    <div className="h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: '#F1F5F9' }}>
                      <div className="h-2.5 rounded-full transition-all"
                        style={{
                          width: `${Math.min(100, target.pct || 0)}%`,
                          background: `linear-gradient(90deg, ${GREEN} 0%, #34D399 100%)`
                        }} />
                    </div>
                    <div className="flex flex-wrap justify-between text-xs mt-1.5 gap-2" style={{ color: '#64748B' }}>
                      <span><strong style={{ color: NAVY }}>{money0(target.achieved)}</strong> invoiced</span>
                      <span>
                        target <strong style={{ color: NAVY }}>{money0(target.revenue)}</strong>
                        {targetGap === 0 ? ' — met' : ` — ${money0(targetGap)} to go`}
                      </span>
                    </div>
                  </>
                ) : (
                  <p className="text-xs" style={{ color: '#94A3B8' }}>
                    No target set for this month. Add one under Settings → Targets and it will appear here.
                  </p>
                )}

                <div className="flex items-end gap-1.5 mt-4" style={{ height: 64 }}>
                  {tva.map((m) => {
                    const actH = Math.round(((m.actual || 0) / tvaMax) * 100);
                    const tgtH = Math.round(((m.target || 0) / tvaMax) * 100);
                    const met = m.pct != null && m.pct >= 100;
                    return (
                      <div key={m.month} className="flex-1 flex flex-col items-center justify-end"
                        title={`${m.label}: ${money0(m.actual)} invoiced${m.target ? ` of ${money0(m.target)}` : ' (no target)'}`}>
                        <div className="w-full relative" style={{ height: 46 }}>
                          {m.target ? (
                            <div className="absolute bottom-0 w-full rounded-sm" style={{ height: `${tgtH}%`, backgroundColor: '#E2E8F0' }} />
                          ) : null}
                          <div className="absolute bottom-0 w-full rounded-sm"
                            style={{ height: `${actH}%`, backgroundColor: met ? GREEN : '#3B82F6', opacity: 0.92 }} />
                        </div>
                        <span className="mt-1 whitespace-nowrap" style={{ color: '#94A3B8', fontSize: 9 }}>{m.label}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>
                  Grey = target · blue = invoiced · green = target met
                </p>
              </div>
            )}
          </Card>

          <Card
            title="Job status"
            subtitle={jobScope === 'month' ? 'Jobs with activity this month — opened, accepted or completed' : 'Every job on record'}
            action={
              <Segmented value={jobScope} onChange={setJobScope} options={[
                { value: 'month', label: 'Month' },
                { value: 'all', label: 'All' }
              ]} />
            }
          >
            <div style={{ height: 260 }}>
              {jobRows.length
                ? <Doughnut data={jobChart} options={doughnutOptions} plugins={[centreTextPlugin]} />
                : <p className="text-sm text-center pt-16" style={{ color: '#94A3B8' }}>No jobs in this period</p>}
            </div>
          </Card>
        </div>
        {/* ---------- top customers + quote requests ---------- */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <Card title="Top customers" subtitle="Revenue, last 12 months (excluding VAT)">
            <div style={{ height: 240 }}>
              {topCustomers.length
                ? <Bar data={customerChart} options={customerOptions} />
                : <p className="text-sm text-center pt-16" style={{ color: '#94A3B8' }}>No invoiced customers yet</p>}
            </div>
          </Card>

          <Card
            title="Quote requests per day"
            subtitle={maxQuotes > 0 ? '🔆 Busiest day highlighted in green' : 'No requests in this period'}
            action={
              <Segmented value={quoteRange} onChange={setQuoteRange} options={[
                { value: '7d', label: '7 days' },
                { value: '30d', label: '30 days' }
              ]} />
            }
          >
            <div style={{ height: 240 }}>
              {quoteSeries.length
                ? <Bar data={quoteChart} options={quoteOptions} />
                : <p className="text-sm text-center pt-16" style={{ color: '#94A3B8' }}>No data</p>}
            </div>
          </Card>
        </div>

        {/* ---------- sales by salesperson + commercial and operational insight ---------- */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          <Card
            className="xl:col-span-2"
            title="Sales by salesperson"
            subtitle={salesCompare
              ? `Paid only — money actually received (excl VAT), by ${salesGran}. Each colour is a salesperson; the pale block is their cost.`
              : `Paid only — money actually received (excl VAT), by ${salesGran}. Full bar is turnover, green is margin.`}
          >
            {/* Filters: who, how fine, and which months. Switching person is instant. */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <select
                value={salesPerson}
                onChange={(e) => setSalesPerson(e.target.value)}
                className="text-xs border rounded-lg px-2 py-1.5"
                style={{ borderColor: '#E8ECEF', color: NAVY }}
              >
                <option value="all">All salespeople — compare</option>
                {salesTeam.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.name}{t.received > 0 ? ` — ${money0(t.received)}` : ' — nothing received'}
                  </option>
                ))}
              </select>

              <select
                value={salesGran}
                onChange={(e) => setSalesGran(e.target.value)}
                className="text-xs border rounded-lg px-2 py-1.5"
                style={{ borderColor: '#E8ECEF', color: NAVY }}
              >
                <option value="day">By day</option>
                <option value="week">By week</option>
                <option value="month">By month</option>
                <option value="year">By year</option>
              </select>

              <div className="flex items-center gap-1 text-xs" style={{ color: '#64748B' }}>
                <input
                  type="month"
                  value={salesFrom}
                  max={salesTo}
                  onChange={(e) => { setSalesPreset(''); setSalesFrom(e.target.value); }}
                  className="border rounded-lg px-2 py-1.5 text-xs"
                  style={{ borderColor: '#E8ECEF', color: NAVY }}
                />
                <span>→</span>
                <input
                  type="month"
                  value={salesTo}
                  min={salesFrom}
                  onChange={(e) => { setSalesPreset(''); setSalesTo(e.target.value); }}
                  className="border rounded-lg px-2 py-1.5 text-xs"
                  style={{ borderColor: '#E8ECEF', color: NAVY }}
                />
              </div>

              <Segmented
                value={salesPreset}
                onChange={applySalesPreset}
                options={[
                  { value: 'm1', label: 'This month' },
                  { value: 'm3', label: '3M' },
                  { value: 'm6', label: '6M' },
                  { value: 'm12', label: '12M' },
                  { value: 'ytd', label: 'YTD' }
                ]}
              />
            </div>

            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
              <p className="text-2xl font-bold" style={{ color: NAVY }}>{money0(salesTotals.turnover)}</p>
              <p className="text-xs" style={{ color: '#64748B' }}>
                received • margin <span style={{ color: GREEN, fontWeight: 600 }}>{money0(salesTotals.margin)}</span>
                {salesMarginPct == null ? '' : ` (${salesMarginPct}%)`} • {salesTotals.payments} payment(s)
              </p>
            </div>

            <div style={{ height: 260 }}>
              {salesLoading ? (
                <p className="text-sm text-center pt-16" style={{ color: '#94A3B8' }}>
                  <FaSpinner className="animate-spin inline" /> Loading…
                </p>
              ) : salesErr ? (
                <p className="text-sm text-center pt-16" style={{ color: '#EF4444' }}>{salesErr}</p>
              ) : salesTotals.turnover > 0 ? (
                <Bar data={salesChart} options={salesOptions} />
              ) : (
                <p className="text-sm text-center pt-16" style={{ color: '#94A3B8' }}>No money received in this period</p>
              )}
            </div>

            {/* The comparison in numbers: the bars show who did what in each period, this shows
                where each person stands across the whole range at a glance. */}
            {salesCompare && (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ color: '#94A3B8' }}>
                      <th className="text-left font-medium py-1">Salesperson</th>
                      <th className="text-right font-medium py-1">Received</th>
                      <th className="text-right font-medium py-1">Margin</th>
                      <th className="text-right font-medium py-1">Margin %</th>
                      <th className="text-right font-medium py-1">Payments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesAll.map((s) => {
                      const key = String(s.id || 'unassigned');
                      return (
                        <tr key={key} className="border-t" style={{ borderColor: '#F1F5F9' }}>
                          <td className="py-1.5" style={{ color: NAVY }}>
                            <span className="inline-flex items-center gap-2">
                              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: colourByKey(key) }} />
                              {s.name}
                            </span>
                          </td>
                          <td className="py-1.5 text-right font-medium" style={{ color: NAVY }}>{money0(s.totals.turnover)}</td>
                          <td className="py-1.5 text-right" style={{ color: GREEN }}>{money0(s.totals.margin)}</td>
                          <td className="py-1.5 text-right" style={{ color: '#64748B' }}>
                            {s.totals.margin_pct == null ? '—' : `${s.totals.margin_pct}%`}
                          </td>
                          <td className="py-1.5 text-right" style={{ color: '#64748B' }}>{s.totals.payments || 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-[11px] mt-2" style={{ color: '#94A3B8' }}>
              Paid means the money is in — each payment is counted on the day it was received. Nothing
              shows until it is paid, and the Revenue MTD card above counts exactly the same money.
            </p>
          </Card>

          <div className="space-y-5">
            <Card title="Quote win rate" subtitle={`${winRate.decided} decided quote(s), last 12 months`}>
              <div className="flex items-end gap-4">
                <p className="text-4xl font-bold" style={{ color: winRate.pct == null ? '#94A3B8' : NAVY }}>
                  {winRate.pct == null ? '—' : `${winRate.pct}%`}
                </p>
                <div className="text-xs pb-1" style={{ color: '#64748B' }}>
                  <p><span className="font-semibold" style={{ color: GREEN }}>{winRate.approved}</span> approved</p>
                  <p><span className="font-semibold" style={{ color: '#EF4444' }}>{winRate.rejected}</span> rejected</p>
                </div>
              </div>
              <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>
                Quotes still out (sent) are excluded — they are not decided yet.
              </p>
            </Card>

            <Card title="Receivables ageing" subtitle={`${money0(k.outstanding_total)} outstanding`}>
              <div className="space-y-2">
                {ageingRows.map((r) => (
                  <div key={r.label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span style={{ color: '#475569' }}>{r.label}</span>
                      <span className="font-medium" style={{ color: r.value > 0 ? NAVY : '#94A3B8' }}>{money0(r.value)}</span>
                    </div>
                    <div className="h-1.5 rounded-full" style={{ backgroundColor: '#F1F5F9' }}>
                      <div className="h-1.5 rounded-full"
                        style={{ width: `${Math.round((r.value / ageingMax) * 100)}%`, backgroundColor: r.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card title="Operations">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#F8FAFC' }}>
                  <p className="text-xs" style={{ color: '#64748B' }}>Fleet utilisation</p>
                  <p className="text-xl font-bold" style={{ color: NAVY }}>
                    {fleet.utilisation_pct == null ? '—' : `${fleet.utilisation_pct}%`}
                  </p>
                  <p className="text-xs" style={{ color: '#94A3B8' }}>{fleet.loads_active} of {fleet.trucks} truck(s)</p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#F8FAFC' }}>
                  <p className="text-xs" style={{ color: '#64748B' }}>On-time delivery</p>
                  <p className="text-xl font-bold" style={{ color: NAVY }}>
                    {onTime.pct == null ? '—' : `${onTime.pct}%`}
                  </p>
                  <p className="text-xs" style={{ color: '#94A3B8' }}>
                    {onTime.scored === 0 ? 'No driver stamps yet' : `${onTime.on_time} of ${onTime.scored} load(s)`}
                  </p>
                </div>
              </div>
              <p className="text-xs mt-3" style={{ color: '#94A3B8' }}>
                On-time is measured from the driver app only — never from typed-in dates.
              </p>
            </Card>
          </div>
        </div>
        {/* ---------- quick actions ---------- */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {[
            { to: '/new-jobs', label: 'New Job', icon: <FaPlusCircle size={16} />, color: GREEN },
            { to: '/quotes', label: 'Quotes', icon: <FaFileInvoice size={16} />, color: '#3B82F6' },
            { to: '/admin-scheduler', label: 'Schedule', icon: <FaCalendarAlt size={16} />, color: '#7C3AED' },
            { to: '/invoices', label: 'Invoices', icon: <FaMoneyBillWave size={16} />, color: '#F59E0B' },
            { to: '/clients', label: 'Clients', icon: <FaUserCheck size={16} />, color: '#0EA5E9' },
            { to: '/fleet-overview', label: 'Fleet', icon: <FaTruck size={16} />, color: '#EC4899' }
          ].map((a) => (
            <Link key={a.label} to={a.to}
              className="rounded-xl p-4 text-white shadow-sm transition-transform hover:-translate-y-0.5"
              style={{ background: `linear-gradient(135deg, ${a.color} 0%, ${a.color}CC 100%)` }}>
              <span className="block mb-2">{a.icon}</span>
              <span className="text-xs font-semibold">{a.label}</span>
            </Link>
          ))}
        </div>

        {/* ---------- upcoming jobs · activity · needs attention ---------- */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          <Card title="Upcoming jobs" subtitle="Starting in the next seven days">
            {(data?.upcoming_jobs || []).length === 0 ? (
              <p className="text-sm" style={{ color: '#94A3B8' }}>Nothing is scheduled to start this week.</p>
            ) : (
              <div className="space-y-3">
                {data.upcoming_jobs.map((j) => (
                  <div key={j.id} className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium" style={{ color: NAVY }}>{j.job_number}</p>
                      <p className="text-xs" style={{ color: '#64748B' }}>
                        {(j.origin || '—')} → {(j.destination || '—')}
                      </p>
                    </div>
                    <span className="text-xs whitespace-nowrap inline-flex items-center gap-1" style={{ color: '#475569' }}>
                      <FaCalendarAlt size={10} />{fmtDay(j.start_date)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card title="Recent activity" subtitle="The latest actions in the system">
            {(data?.recent_activity || []).length === 0 ? (
              <p className="text-sm" style={{ color: '#94A3B8' }}>No activity recorded yet.</p>
            ) : (
              <div className="space-y-3">
                {data.recent_activity.map((a, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <FaHistory size={11} className="mt-1 flex-shrink-0" style={{ color: '#94A3B8' }} />
                    <div>
                      <p className="text-xs" style={{ color: '#475569' }}>
                        <span className="font-medium capitalize" style={{ color: NAVY }}>{a.action}</span>
                        {a.entity_type ? ` · ${String(a.entity_type).replace(/_/g, ' ')}` : ''}
                      </p>
                      <p className="text-xs inline-flex items-center gap-1" style={{ color: '#94A3B8' }}>
                        <FaClock size={9} />{timeAgo(a.created_at)}
                        {a.user_email ? ` · ${a.user_email.split('@')[0]}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card title="Needs attention" subtitle="Worth a look, sir">
            {(data?.needs_attention || []).length === 0 ? (
              <p className="text-sm" style={{ color: '#94A3B8' }}>Nothing requires attention. A rare pleasure.</p>
            ) : (
              <div className="space-y-3">
                {data.needs_attention.map((n) => (
                  <Link key={n.key} to={n.href} className="flex items-start justify-between gap-3 group">
                    <span className="flex items-start gap-2">
                      <FaExclamationTriangle size={11} className="mt-1 flex-shrink-0" style={{ color: '#F59E0B' }} />
                      <span>
                        <span className="text-xs font-medium block" style={{ color: NAVY }}>{n.label}</span>
                        <span className="text-xs" style={{ color: '#94A3B8' }}>
                          {n.amount ? `${money0(n.amount)} overdue` : n.detail}
                        </span>
                      </span>
                    </span>
                    <span className="text-xs opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: GREEN }}>view</span>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminDashboard;

